import React from "react";

import DynamicTableVirtual from "../components/table-virtual";
import Topbar from "../components/topbar";
import { useListSearch, ListTitle } from "../components/ui/list-search";

import type { Column } from '../components/table-virtual';
import AddBtn from "../components/ui/add-btn";
import DeletePopup from "../components/popup/delete";
import DeletePopupMobile from "../components/popup/mobile/delete-mobile";
import bankImage from '../files/images/bank-account.png';
import AddBankAccountPopup from "../components/popup/add-bank-account";
import { useUser } from "../providers/user-provider";
import { usePermissions } from "../providers/permissions-provider";
import { useMediaQuery, useTheme, Button, Box, Fab } from "@mui/material";
import { Menu, Plus } from "lucide-react";
import SidebarMobileDrawer from "../components/sidebar-mobile-drawer";
import { useSidebarMobile } from "../providers/sidebar-mobile-context";
import useApi from '../hooks/use-api';
import buildApiRequestConfig from '../api/url-builder';
import routes from '../api/routes';
import { useToast } from "../providers/toaster-provider";

type BankAccount = {
    id: number;
    balance: number;
    name: string;
    bank: string;
    bankId: number;
    autoGeneratedMeta?: string;
    transactions?: {
        count: number;
        firstUsed: string | null;
    };
};

// Bank accounts created automatically by an integration (e.g. Stripe) are
// tagged via autoGeneratedMeta so they stay identifiable independent of the
// user-editable account name (same convention as ProvisionTemplate.autoProvisionMeta).
const isAutoGeneratedAccount = (row: Record<string, any>): boolean => {
    if (!row?.autoGeneratedMeta) return false;
    try {
        return !!JSON.parse(row.autoGeneratedMeta)?.source;
    } catch {
        return false;
    }
};

const BankAccounts: React.FC = () => {
    const { user } = useUser();
    const { can } = usePermissions();
    const canCreate = can('bank_accounts', 'create');
    const canUpdate = can('bank_accounts', 'update');
    const canDelete = can('bank_accounts', 'delete');
    const { showSuccess, showError } = useToast();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const DeleteModal = isMobile ? DeletePopupMobile : DeletePopup;
    const { sidebarOpen, setSidebarOpen } = useSidebarMobile();
    const [data, setData] = React.useState<BankAccount[]>([]);
    const list = useListSearch(data);
    const [openModal, setOpenModal] = React.useState<string>('');
    const [selectedAccount, setSelectedAccount] = React.useState<BankAccount | null>(null);

    const [fetch, setFetch] = useApi();
    const [create, setCreate] = useApi();
    const [update, setUpdate] = useApi();
    const [remove, setRemove] = useApi();

    React.useEffect(() => {
        document.title = "FineAnts | Bank Accounts";
    }, []);

    React.useEffect(() => {
        const { url, method } = buildApiRequestConfig(routes.bankAccounts.fetchByEntity, [{ param: 'entityId', value: user?.entityId }]);
        setFetch({ url, method });
    }, [create, update, remove, user]);

    React.useEffect(() => {
        const { data, isLoading, error } = fetch;
        if (data && !isLoading) {
            setData(data.map((account: any) => ({
                id: account.id,
                balance: account.balance,
                name: account.name,
                bank: account.bank,
                bankId: account.bankId,
                autoGeneratedMeta: account.autoGeneratedMeta,
            })));
        }
        if (error) {
            showError("Failed to fetch bank accounts");
        }
    }, [fetch]);

    React.useEffect(() => {
        const { data, isLoading, error } = create;
        if (data && !isLoading) {
            showSuccess("Bank account added successfully");
            setOpenModal('');
        }
        if (error) {
            showError(typeof error === 'string' ? error : "Failed to add bank account. Please try again.");
        }
    }, [create]);

    React.useEffect(() => {
        const { data, isLoading, error } = update;
        if (data && !isLoading) {
            setSelectedAccount(null);
            setOpenModal('');
            showSuccess("Bank account updated successfully");
        }
        if (error) {
            showError(typeof error === 'string' ? error : "Failed to update bank account");
        }
    }, [update]);

    React.useEffect(() => {
        const { data, isLoading, error } = remove;
        if (data && !isLoading) {
            setSelectedAccount(null);
            setOpenModal('');
            showSuccess("Bank account deleted successfully");
        }
        if (error) {
            showError(typeof error === 'string' ? error : "Failed to delete bank account");
        }
    }, [remove]);


    const handleCreate = (accountData: { name: string; bankId: number | null; openingBalance: number }) => {
        const { url, method } = buildApiRequestConfig(routes.bankAccounts.create);
        setCreate({ url, method, body: {
            ...accountData, entityId: user?.entityId, accountName: accountData.name
        }});
    }

    const handleUpdate = (accountData: { name: string; bankId: number | null; openingBalance: number }) => {
        if(!selectedAccount) return;
        const { url, method } = buildApiRequestConfig(routes.bankAccounts.update, [{ param: 'id', value: selectedAccount.id }]);
        setUpdate({ url, method, body: {
            ...accountData, accountName: accountData.name,
        } });
    }

    const handleDelete = () => {
        if(!selectedAccount) return;
        const { url, method } = buildApiRequestConfig(routes.bankAccounts.delete, [{ param: 'id', value: selectedAccount.id }]);
        setRemove({ url, method });
    }

    const columns: Column[] = [
        { id: 'bank', label: 'Bank', size: 150, onSort: (direction) => console.log(`Sort by amount ${direction}`) },
        { id: 'name', label: 'Account Name', size: 150, onSort: (direction) => console.log(`Sort by amount ${direction}`) },
        {
            id: 'actions',
            label: 'Actions',
            size: 50,
            type: 'actions',
            canDelete: (row) => !isAutoGeneratedAccount(row),
            onDelete: (row) => {setOpenModal('delete'); setSelectedAccount(row as BankAccount);},
            onEdit: (row) => {
                setSelectedAccount(row as BankAccount);
                setOpenModal('edit');
            },
        },
    ];

    return (
        <div style={{ width: '100%' }}>
            <AddBankAccountPopup
                open={openModal === 'add' || openModal === 'edit'}
                onClose={() => setOpenModal('')}
                onCreate={(newAccount) => {
                    if(openModal === 'edit') {
                        handleUpdate(newAccount);
                    } else {
                        handleCreate(newAccount);
                    }
                }}
                isEdit={{ id: selectedAccount?.id || null, state: openModal === 'edit' }}
                existing={data.map(item => ({ id: item.id, name: item.name, bankId: item.bankId }))}
            />
            <DeleteModal
                open={openModal === 'delete'}
                message="Delete Bank Account?"
                description={`This action will permanently remove the selected bank account.<br/> This account has ${selectedAccount?.transactions?.count ?? 0} transaction(s) ${selectedAccount?.transactions?.firstUsed ? `from ${selectedAccount?.transactions?.firstUsed} to ${new Date().getFullYear()}.` : ''}`}
                image={{ src: bankImage, width: 150, height: 150 }}
                onCancel={() => setOpenModal('')}
                onConfirm={() => {
                    handleDelete();
                }}
            />
            <Topbar
                title={<ListTitle title="Bank Accounts" list={list} />}
                isMobile={isMobile}
                mobileMenuButton={
                    isMobile && (
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={() => setSidebarOpen(true)} 
                            sx={{ 
                                color: '#fff', 
                                height: 40,
                                display: 'flex',
                                gap: 1,
                                minWidth: 40,
                                p: 1,
                                backgroundColor: 'var(--color-primary)', 
                                '&:hover': { backgroundColor: 'var(--color-primary)' } 
                            }}
                        >
                            <Menu size={20} color="#fff"/>
                        </Button>
                    )
                }
            >
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%', justifyContent: 'flex-end' }}>
                    {!isMobile && (
                        <AddBtn disabled={!canCreate} onClick={() => {setOpenModal('add'); setSelectedAccount(null);}} label="Add Account" />
                    )}
                </Box>
            </Topbar>
            <div style={{ padding: '10px' }}>
                <DynamicTableVirtual columns={columns} data={list.filtered} isLoading={fetch.isLoading} scrollMode="page" canEditRows={canUpdate} canDeleteRows={canDelete}/>
            </div>
            <SidebarMobileDrawer
                open={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
            />
            {isMobile && canCreate && (
                <Fab
                    onClick={() => {setOpenModal('add'); setSelectedAccount(null);}}
                    sx={{
                        position: 'fixed',
                        bottom: 20,
                        right: 20,
                        background: 'var(--color-primary)',
                        color: '#fff',
                        '&:hover': { background: 'var(--color-primary-hover)' },
                        zIndex: 999,
                    }}
                >
                    <Plus size={24} />
                </Fab>
            )}
        </div>
    );
};

export default BankAccounts;